import React, { useRef, useState } from 'react';
import { ACTIVITY_COMMENT_MAX_LENGTH } from '../../utils/activityComment';

interface ActivityCommentLineProps {
    comment: string | undefined;
    isEditing: boolean;
    disabled: boolean;
    onStartEdit: () => void;
    /** Leaving the field saves; an empty text removes the comment */
    onSave: (text: string) => void;
    onCancel: () => void;
}

/**
 * The row's comment as a hint line under the activity, spanning the whole row.
 * Clicking it (or the row's comment icon) turns it into a textarea in place.
 */
export const ActivityCommentLine: React.FC<ActivityCommentLineProps> = ({
    comment,
    isEditing,
    disabled,
    onStartEdit,
    onSave,
    onCancel
}) => {
    if (isEditing) {
        return (
            <div className="activity-comment-line">
                <ActivityCommentEditor initialText={comment ?? ''} onSave={onSave} onCancel={onCancel} />
            </div>
        );
    }

    if (!comment) return null;

    return (
        <div className="activity-comment-line">
            <button
                type="button"
                className="activity-comment-text"
                onClick={onStartEdit}
                disabled={disabled}
                title="Redigera kommentar"
            >
                {comment}
            </button>
        </div>
    );
};

interface ActivityCommentEditorProps {
    initialText: string;
    onSave: (text: string) => void;
    onCancel: () => void;
}

/**
 * Mounted per edit, so every edit starts from the stored comment.
 * Blur saves and Esc cancels. Nothing is saved on unmount: StrictMode runs
 * effect cleanups on mount in development, which would close the field at once.
 */
const ActivityCommentEditor: React.FC<ActivityCommentEditorProps> = ({ initialText, onSave, onCancel }) => {
    const [text, setText] = useState(initialText);
    // Set once the edit has ended by blur or Esc, so it is not ended twice
    const finished = useRef(false);

    return (
        <textarea
            className="form-control form-control-sm activity-comment-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => {
                // The window lost focus (another app): the field stays open and keeps focus on return
                if (!document.hasFocus()) return;
                if (finished.current) return;
                finished.current = true;
                onSave(text);
            }}
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    finished.current = true;
                    onCancel();
                }
            }}
            maxLength={ACTIVITY_COMMENT_MAX_LENGTH}
            rows={2}
            placeholder="Kommentar till aktiviteten"
            aria-label="Kommentar till aktiviteten"
            autoFocus
        />
    );
};
